import { createContext, ReactNode, useContext } from 'react';
import { apiV2 } from '../services/api';

type EntityId = string | number;

type ApiResponse<T = any> = {
  data?: T;
  message?: string;
  status?: number;
  success?: boolean;
};

export interface ProvidedServiceBoat {
  id_provided_service_boat: EntityId;
  harvest: number;
  fk_cost_center_id: EntityId;
  fk_boat_id: EntityId;
  fk_trip_id: EntityId;
}

export type ProvidedServiceBoatInput = {
  id_provided_service_boat?: EntityId;
  fk_cost_center_id: EntityId;
  fk_trip_id: EntityId;
};

export interface ProvidedService {
  id_provided_service: EntityId;
  start_datetime: string;
  end_datetime: string;
  value: number;
  discount: number;
  is_indirect_cost: boolean;
  boats?: ProvidedServiceBoat[];
}

export interface ProvidedServiceEmployee {
  id_provided_service_employee: EntityId;
  fk_employee_id: EntityId;
  fk_cost_category_id: EntityId;
  services?: ProvidedService[];
}

export interface ProvidedServiceBatch {
  id_provided_service_batch: EntityId;
  competence_start_date: string;
  competence_end_date: string;
  fk_payment_type_id: EntityId;
  expiration_date: string;
  status?: 'DRAFT' | 'CLOSED' | 'EXECUTED';
  employees?: ProvidedServiceEmployee[];
  canClose?: boolean;
  canReopen?: boolean;
  canExecute?: boolean;
}

export type ProvidedServiceInput = Omit<ProvidedService, 'id_provided_service' | 'boats'> & {
  boats: ProvidedServiceBoatInput[];
};
export type ProvidedServiceEmployeeInput = Omit<ProvidedServiceEmployee, 'id_provided_service_employee' | 'services'> & {
  services?: ProvidedServiceInput[];
};
export type ProvidedServiceBatchInput = Omit<ProvidedServiceBatch, 'id_provided_service_batch' | 'employees'> & {
  employees?: ProvidedServiceEmployeeInput[];
};
export type ExecuteServiceBatchInput = {
  payment_date: string;
};

export interface ServicesProviderFilter {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  expirationStartDate?: string;
  expirationEndDate?: string;
  employeeId?: EntityId;
  paymentTypeId?: EntityId;
  costCategoryId?: EntityId;
  costCenterId?: EntityId;
  harvest?: number;
  boatId?: EntityId;
  tripId?: EntityId;
  isIndirectCost?: boolean;
  status?: string;
}

export interface ServicesProviderResponse<T = undefined> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ServicesProviderPaginatedResponse<T> {
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  data: T[];
}

interface ServicesProviderProps {
  children: ReactNode;
}

interface ServicesProviderContextData {
  getServiceBatches: (filter?: ServicesProviderFilter) => Promise<ServicesProviderResponse<ServicesProviderPaginatedResponse<ProvidedServiceBatch>>>;
  getServiceBatch: (batchId: EntityId) => Promise<ServicesProviderResponse<ProvidedServiceBatch>>;
  createServiceBatch: (batch: ProvidedServiceBatchInput) => Promise<ServicesProviderResponse<ProvidedServiceBatch>>;
  updateServiceBatch: (batchId: EntityId, batch: ProvidedServiceBatchInput) => Promise<ServicesProviderResponse<ProvidedServiceBatch>>;
  closeServiceBatch: (batchId: EntityId) => Promise<ServicesProviderResponse<ProvidedServiceBatch>>;
  reopenServiceBatch: (batchId: EntityId) => Promise<ServicesProviderResponse<ProvidedServiceBatch>>;
  executeServiceBatch: (batchId: EntityId, input: ExecuteServiceBatchInput) => Promise<ServicesProviderResponse>;
  deleteServiceBatch: (batchId: EntityId) => Promise<ServicesProviderResponse>;

  getBatchEmployees: (batchId: EntityId) => Promise<ServicesProviderResponse<ProvidedServiceEmployee[]>>;
  getBatchEmployee: (batchId: EntityId, batchEmployeeId: EntityId) => Promise<ServicesProviderResponse<ProvidedServiceEmployee>>;
  createBatchEmployee: (batchId: EntityId, employee: ProvidedServiceEmployeeInput) => Promise<ServicesProviderResponse<ProvidedServiceEmployee>>;
  updateBatchEmployee: (batchId: EntityId, batchEmployeeId: EntityId, employee: ProvidedServiceEmployeeInput) => Promise<ServicesProviderResponse<ProvidedServiceEmployee>>;
  deleteBatchEmployee: (batchId: EntityId, batchEmployeeId: EntityId) => Promise<ServicesProviderResponse>;

  getProvidedServices: (batchId: EntityId, batchEmployeeId: EntityId) => Promise<ServicesProviderResponse<ProvidedService[]>>;
  getProvidedService: (batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId) => Promise<ServicesProviderResponse<ProvidedService>>;
  createProvidedService: (batchId: EntityId, batchEmployeeId: EntityId, service: ProvidedServiceInput) => Promise<ServicesProviderResponse<ProvidedService>>;
  updateProvidedService: (batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId, service: ProvidedServiceInput) => Promise<ServicesProviderResponse<ProvidedService>>;
  deleteProvidedService: (batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId) => Promise<ServicesProviderResponse>;
}

const ServicesProviderContext = createContext<ServicesProviderContextData>(
  {} as ServicesProviderContextData
);

// Rotas implementadas no módulo provided-services da API v2.
export const servicesProviderRoutes = {
  batches: '/provided-service-batches',
  batch: (batchId: EntityId) => `/provided-service-batches/${batchId}`,
  closeBatch: (batchId: EntityId) => `/provided-service-batches/${batchId}/close`,
  reopenBatch: (batchId: EntityId) => `/provided-service-batches/${batchId}/reopen`,
  executeBatch: (batchId: EntityId) => `/provided-service-batches/${batchId}/execute`,
  employees: (batchId: EntityId) => `/provided-service-batches/${batchId}/employees`,
  employee: (batchId: EntityId, batchEmployeeId: EntityId) =>
    `/provided-service-batches/${batchId}/employees/${batchEmployeeId}`,
  services: (batchId: EntityId, batchEmployeeId: EntityId) =>
    `/provided-service-batches/${batchId}/employees/${batchEmployeeId}/services`,
  service: (batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId) =>
    `/provided-service-batches/${batchId}/employees/${batchEmployeeId}/services/${serviceId}`
};

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^user credentials do not match\.?$/i.test(normalizedMessage)) {
    return 'Credenciais de usuário inválidas.';
  }

  if (/^provided service .+ has no trips\.$/i.test(normalizedMessage)) {
    return 'Não foi possível fechar o lote porque existe serviço prestado sem barco/viagem associado.';
  }

  if (/^the batch has no employees\.$/i.test(normalizedMessage)) {
    return 'Não foi possível fechar o lote porque ele não possui colaboradores associados.';
  }

  if (/^employee .+ has no provided services\.$/i.test(normalizedMessage)) {
    return 'Não foi possível fechar o lote porque existe colaborador sem serviço prestado cadastrado.';
  }

  if (/^service batch is already closed\.$/i.test(normalizedMessage) ||
    /^only draft service batches can be closed\.$/i.test(normalizedMessage)) {
    return 'Este lote de serviços prestados já está fechado.';
  }

  if (/^closed service batches cannot be modified\.$/i.test(normalizedMessage)) {
    return 'Este lote de serviços prestados não pode ser alterado neste status.';
  }

  if (/^only closed service batches can be reopened\.$/i.test(normalizedMessage) ||
    /^only closed and executed service batches can be reopened\.$/i.test(normalizedMessage)) {
    return 'Apenas lotes de serviços prestados fechados ou executados podem ser reabertos.';
  }

  if (/^only closed (service )?batches can have their orders executed\.?$/i.test(normalizedMessage) ||
    /^only closed service batches can be executed\.$/i.test(normalizedMessage)) {
    return 'Apenas lotes de serviços prestados fechados podem ter os pedidos de remuneração executados.';
  }

  if (/^service batch not found\.?$/i.test(normalizedMessage)) {
    return 'Lote de serviços prestados não encontrado.';
  }

  if (/^batch execution is not allowed for installment orders\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível executar o lote porque existem pedidos de remuneração parcelados.';
  }

  if (/payment[_\s-]?date/i.test(normalizedMessage) && /(required|obrigat|invalid|válid|valid)/i.test(normalizedMessage)) {
    return 'Informe uma data de pagamento válida para executar os pedidos de remuneração do lote.';
  }

  if (/(already|já).*(executed|executad)/i.test(normalizedMessage) && /(order|pedido|remunera|salary)/i.test(normalizedMessage)) {
    return 'Não foi possível executar o lote porque existe pedido de remuneração que já foi executado.';
  }

  if (/(no|nenhum).*(order|pedido|remunera|salary)/i.test(normalizedMessage)) {
    return 'Não foi possível executar o lote porque não há pedidos de remuneração relacionados.';
  }

  if (/execut/i.test(normalizedMessage) && /(order|pedido|remunera|salary)/i.test(normalizedMessage)) {
    return 'Não foi possível concluir a operação porque existe pedido de remuneração relacionado que já foi executado.';
  }

  return undefined;
}

function toResponse<T>(response: any, successMessage: string, errorMessage: string): ServicesProviderResponse<T> {
  const success = response?.success !== false && response?.status >= 200 && response?.status < 300;
  const apiMessage = response?.data?.message || response?.message;
  const translatedMessage = translateKnownApiMessage(apiMessage);
  const hasDataProperty = response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data');
  const payload = hasDataProperty ? response.data.data : response?.data;

  return {
    success,
    message: success ? successMessage : translatedMessage || (isTechnicalMessage(apiMessage) ? errorMessage : apiMessage),
    data: success ? payload : undefined
  };
}

function removeEmptyFilters(filter: ServicesProviderFilter) {
  return Object.entries(filter).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (value !== "''" && value !== '' && value !== undefined && value !== null) {
      params[key] = value;
    }
    return params;
  }, {});
}

export function ServicesProvider({ children }: ServicesProviderProps) {
  async function getServiceBatches(filter: ServicesProviderFilter = {}) {
    const response = await apiV2.get(servicesProviderRoutes.batches, { params: removeEmptyFilters(filter) }) as ApiResponse;
    const apiMessage = response?.data?.message || response?.message;
    const translatedMessage = translateKnownApiMessage(apiMessage);
    const status = response?.status ?? 0;
    const success = response?.success !== false && status >= 200 && status < 300;

    return {
      success,
      message: success ? 'Lotes consultados com sucesso!' : translatedMessage || (isTechnicalMessage(apiMessage) ? 'Não foi possível consultar os lotes de serviços prestados.' : apiMessage),
      data: success ? response?.data : undefined
    };
  }

  async function getServiceBatch(batchId: EntityId) {
    const response = await apiV2.get(servicesProviderRoutes.batch(batchId));
    return toResponse<ProvidedServiceBatch>(response, 'Lote consultado com sucesso!', 'Não foi possível consultar o lote de serviços prestados.');
  }

  async function createServiceBatch(batch: ProvidedServiceBatchInput) {
    const response = await apiV2.post(servicesProviderRoutes.batches, batch);
    return toResponse<ProvidedServiceBatch>(response, 'Lote de serviços prestados adicionado com sucesso!', 'Não foi possível adicionar o lote de serviços prestados.');
  }

  async function updateServiceBatch(batchId: EntityId, batch: ProvidedServiceBatchInput) {
    const response = await apiV2.put(servicesProviderRoutes.batch(batchId), batch);
    return toResponse<ProvidedServiceBatch>(response, 'Lote de serviços prestados alterado com sucesso!', 'Não foi possível alterar o lote de serviços prestados.');
  }

  async function closeServiceBatch(batchId: EntityId) {
    const response = await apiV2.post(servicesProviderRoutes.closeBatch(batchId));
    return toResponse<ProvidedServiceBatch>(response, 'Lote de serviços prestados fechado com sucesso!', 'Não foi possível fechar o lote de serviços prestados.');
  }

  async function reopenServiceBatch(batchId: EntityId) {
    const response = await apiV2.post(servicesProviderRoutes.reopenBatch(batchId));
    return toResponse<ProvidedServiceBatch>(response, 'Lote de serviços prestados reaberto com sucesso!', 'Não foi possível reabrir o lote de serviços prestados.');
  }

  async function executeServiceBatch(batchId: EntityId, input: ExecuteServiceBatchInput) {
    const response = await apiV2.post(servicesProviderRoutes.executeBatch(batchId), input);
    return toResponse<undefined>(response, 'Pedidos de remuneração do lote executados com sucesso!', 'Não foi possível executar os pedidos de remuneração do lote.');
  }

  async function deleteServiceBatch(batchId: EntityId) {
    const response = await apiV2.delete(servicesProviderRoutes.batch(batchId));
    return toResponse<undefined>(response, 'Lote de serviços prestados removido com sucesso!', 'Não foi possível remover o lote de serviços prestados.');
  }

  async function getBatchEmployees(batchId: EntityId) {
    const response = await apiV2.get(servicesProviderRoutes.employees(batchId));
    return toResponse<ProvidedServiceEmployee[]>(response, 'Colaboradores consultados com sucesso!', 'Não foi possível consultar os colaboradores do lote.');
  }

  async function getBatchEmployee(batchId: EntityId, batchEmployeeId: EntityId) {
    const response = await apiV2.get(servicesProviderRoutes.employee(batchId, batchEmployeeId));
    return toResponse<ProvidedServiceEmployee>(response, 'Colaborador consultado com sucesso!', 'Não foi possível consultar o colaborador do lote.');
  }

  async function createBatchEmployee(batchId: EntityId, employee: ProvidedServiceEmployeeInput) {
    const response = await apiV2.post(servicesProviderRoutes.employees(batchId), employee);
    return toResponse<ProvidedServiceEmployee>(response, 'Colaborador associado ao lote com sucesso!', 'Não foi possível associar o colaborador ao lote.');
  }

  async function updateBatchEmployee(batchId: EntityId, batchEmployeeId: EntityId, employee: ProvidedServiceEmployeeInput) {
    const response = await apiV2.put(servicesProviderRoutes.employee(batchId, batchEmployeeId), employee);
    return toResponse<ProvidedServiceEmployee>(response, 'Colaborador do lote alterado com sucesso!', 'Não foi possível alterar o colaborador do lote.');
  }

  async function deleteBatchEmployee(batchId: EntityId, batchEmployeeId: EntityId) {
    const response = await apiV2.delete(servicesProviderRoutes.employee(batchId, batchEmployeeId));
    return toResponse<undefined>(response, 'Colaborador removido do lote com sucesso!', 'Não foi possível remover o colaborador do lote.');
  }

  async function getProvidedServices(batchId: EntityId, batchEmployeeId: EntityId) {
    const response = await apiV2.get(servicesProviderRoutes.services(batchId, batchEmployeeId));
    return toResponse<ProvidedService[]>(response, 'Serviços prestados consultados com sucesso!', 'Não foi possível consultar os serviços prestados.');
  }

  async function getProvidedService(batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId) {
    const response = await apiV2.get(servicesProviderRoutes.service(batchId, batchEmployeeId, serviceId));
    return toResponse<ProvidedService>(response, 'Serviço prestado consultado com sucesso!', 'Não foi possível consultar o serviço prestado.');
  }

  async function createProvidedService(batchId: EntityId, batchEmployeeId: EntityId, service: ProvidedServiceInput) {
    const response = await apiV2.post(servicesProviderRoutes.services(batchId, batchEmployeeId), service);
    return toResponse<ProvidedService>(response, 'Serviço prestado adicionado com sucesso!', 'Não foi possível adicionar o serviço prestado.');
  }

  async function updateProvidedService(batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId, service: ProvidedServiceInput) {
    const response = await apiV2.put(servicesProviderRoutes.service(batchId, batchEmployeeId, serviceId), service);
    return toResponse<ProvidedService>(response, 'Serviço prestado alterado com sucesso!', 'Não foi possível alterar o serviço prestado.');
  }

  async function deleteProvidedService(batchId: EntityId, batchEmployeeId: EntityId, serviceId: EntityId) {
    const response = await apiV2.delete(servicesProviderRoutes.service(batchId, batchEmployeeId, serviceId));
    return toResponse<undefined>(response, 'Serviço prestado removido com sucesso!', 'Não foi possível remover o serviço prestado.');
  }

  return (
    <ServicesProviderContext.Provider value={{
      getServiceBatches,
      getServiceBatch,
      createServiceBatch,
      updateServiceBatch,
      closeServiceBatch,
      reopenServiceBatch,
      executeServiceBatch,
      deleteServiceBatch,
      getBatchEmployees,
      getBatchEmployee,
      createBatchEmployee,
      updateBatchEmployee,
      deleteBatchEmployee,
      getProvidedServices,
      getProvidedService,
      createProvidedService,
      updateProvidedService,
      deleteProvidedService
    }}>
      {children}
    </ServicesProviderContext.Provider>
  );
}

export function UseServicesProvider() {
  return useContext(ServicesProviderContext);
}
